// Lectura y escrituras de los referidos del cajón. Se cae por su cuenta: no vacía las otras
// pestañas. Agregar y corregir refrescan la lista.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createCustomerReferrals,
    listCustomerReferrals,
    updateCustomerReferral,
    type BackofficeCustomerReferrals,
    type CreateCustomerReferralInput,
    type UpdateCustomerReferralInput,
} from '@/api/services/retail-wizard/customerReferrals.client';
import { referralsFailureOf, type ReferralsFailure } from './customerReferrals';

const KEY = ['customer-referrals'] as const;

const STALE_TIME_MS = 60_000;

/**
 * La cadena de la lectura. `undefined` = la pone el token (Admin VL); un id viaja en `?value=`
 * (mesa central); `null` = no hay con qué pedirla y la lectura no sale.
 */
export interface ReferralsScope {
    retailerId?: string | null;
    /** La cadena todavía se resuelve: se reporta cargando, no «sin cadena». */
    isScopePending?: boolean;
}

export interface CustomerReferralsState {
    data: BackofficeCustomerReferrals | undefined;
    hasNoScope: boolean;
    isLoading: boolean;
    failure: ReferralsFailure | null;
    refetch: () => void;
}

function keyOf(retailCustomerId: string | null, retailerId: string | null | undefined) {
    return [...KEY, retailCustomerId, retailerId ?? null] as const;
}

export function useCustomerReferrals(
    retailCustomerId: string | null,
    { retailerId, isScopePending = false }: ReferralsScope = {},
): CustomerReferralsState {
    const enabled = retailCustomerId !== null && retailerId !== null;
    const lacksScope = retailCustomerId !== null && retailerId === null;

    const query = useQuery({
        queryKey: keyOf(retailCustomerId, retailerId),
        queryFn: () => listCustomerReferrals(retailCustomerId ?? '', retailerId ?? undefined),
        enabled,
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    return {
        data: query.data,
        hasNoScope: lacksScope && !isScopePending,
        isLoading: (enabled && query.isPending) || (lacksScope && isScopePending),
        failure: referralsFailureOf(query.error),
        refetch: () => {
            void query.refetch();
        },
    };
}

export function useCreateCustomerReferral(retailCustomerId: string, retailerId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateCustomerReferralInput) =>
            createCustomerReferrals(retailCustomerId, [input], retailerId),
        onSettled: () =>
            queryClient.invalidateQueries({ queryKey: keyOf(retailCustomerId, retailerId) }),
    });
}

export function useUpdateCustomerReferral(retailCustomerId: string, retailerId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            referralId,
            patch,
        }: {
            referralId: string;
            patch: UpdateCustomerReferralInput;
        }) => updateCustomerReferral(retailCustomerId, referralId, patch, retailerId),
        onSettled: () =>
            queryClient.invalidateQueries({ queryKey: keyOf(retailCustomerId, retailerId) }),
    });
}
